import axios, { AxiosError } from 'axios';

/**
 * Função utilitária para tratar erros provenientes de requisições Axios.
 *
 * Essa função padroniza o formato de erros lançados pela aplicação,
 * tornando-os consistentes em todos os serviços que utilizam Axios.
 *
 * @param error - O erro capturado durante uma requisição Axios.
 * @param resource - O endpoint (recurso) da API em que ocorreu o erro.
 *
 * @throws Um objeto padronizado contendo informações sobre o erro:
 * - `path`: string → o recurso/endpoint da requisição.
 * - `status`: number | undefined → o código HTTP retornado (se disponível).
 * - `data`: unknown → o payload de resposta do erro (se disponível).
 * - `message`: string → mensagem amigável ou a mensagem retornada pela API.
 *
 * @example
 * ```ts
 * import { handleAxiosErrorUtil } from '@/utils/AxiosUtil';
 *
 * ApiService.vueInstance.axios.post('/config', payload)
 *   .catch((error) => handleAxiosErrorUtil(error, '/config'));
 * ```
 */
export function handleAxiosErrorUtil(error: unknown, resource: string): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    throw {
      path: resource,
      status: axiosError.response?.status,
      data: axiosError.response?.data,
      message:
        (axiosError.response?.data as any)?.message ||
        'Erro na comunicação com o servidor.',
    };
  } else {
    throw {
      path: resource,
      message: 'Erro inesperado ao processar a requisição.',
    };
  }
}
